export interface FormatContext {
  /** BCP 47 locale for number and date conventions, normally the club's. */
  locale: string;
  /** IANA time zone of the club. */
  timeZone: string;
  /** ISO 4217 currency code of the club. */
  currency: string;
}

const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/;

function safeLocale(locale: string): string {
  try {
    return Intl.getCanonicalLocales(locale)[0] ?? "en-US";
  } catch {
    return "en-US";
  }
}

export function currencyDigits(locale: string, currency: string): number {
  return new Intl.NumberFormat(safeLocale(locale), { style: "currency", currency }).resolvedOptions().maximumFractionDigits ?? 2;
}

export function createFormatters(context: FormatContext) {
  const locale = safeLocale(context.locale);
  const { timeZone, currency } = context;

  // Intl formatters are slow to build; a full tee sheet formats hundreds of values, so build each once.
  const cache = new Map<string, Intl.DateTimeFormat>();
  const dateTimeFormat = (key: string, options: Intl.DateTimeFormatOptions) => {
    let format = cache.get(key);
    if (!format) {
      format = new Intl.DateTimeFormat(locale, options);
      cache.set(key, format);
    }
    return format;
  };
  const moneyFormat = new Intl.NumberFormat(locale, { style: "currency", currency });
  const moneyDigits = currencyDigits(locale, currency);

  function money(minor: number): string {
    return moneyFormat.format((minor || 0) / 10 ** moneyDigits); // || 0: minus zero prints as "-$0.00"
  }

  /** Calendar dates without a time ("2024-05-01") are shown as written, never shifted by a time zone. */
  function date(value: string | Date | null | undefined, style: "short" | "medium" | "long" = "medium"): string {
    if (!value) return "";
    const match = typeof value === "string" ? dateOnly.exec(value) : null;
    if (match) {
      const utc = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
      return dateTimeFormat(`date-utc-${style}`, { dateStyle: style, timeZone: "UTC" }).format(utc);
    }
    return dateTimeFormat(`date-${style}`, { dateStyle: style, timeZone }).format(new Date(value));
  }

  /** The month and day of a calendar date ("June 5"), for an anniversary whose year is not known. */
  function monthDay(value: string | null | undefined): string {
    const match = value ? dateOnly.exec(value) : null;
    if (!match) return "";
    const utc = new Date(Date.UTC(2000, Number(match[2]) - 1, Number(match[3])));
    return dateTimeFormat("month-day", { month: "long", day: "numeric", timeZone: "UTC" }).format(utc);
  }

  /** The names of the twelve months in the interface's language, January first. */
  function monthNames(): string[] {
    return Array.from({ length: 12 }, (_, index) => dateTimeFormat("month-name", { month: "long", timeZone: "UTC" }).format(new Date(Date.UTC(2000, index, 1))));
  }

  function dateTime(value: string | Date | null | undefined): string {
    if (!value) return "";
    return dateTimeFormat("datetime", { dateStyle: "medium", timeStyle: "short", timeZone }).format(new Date(value));
  }

  function time(value: string | Date | null | undefined): string {
    if (!value) return "";
    return dateTimeFormat("time", { timeStyle: "short", timeZone }).format(new Date(value));
  }

  /** A club-local wall-clock time such as "07:08:00" (a tee time): shown as written, never shifted by a time zone. */
  function wallTime(value: string | null | undefined): string {
    if (!value) return "";
    const [h = "0", m = "0"] = value.split(":");
    return dateTimeFormat("wall", { timeStyle: "short", timeZone: "UTC" }).format(new Date(Date.UTC(1970, 0, 1, Number(h), Number(m))));
  }

  /** Weekday name for an ISO weekday number, 1 (Monday) to 7 (Sunday). */
  function weekday(iso: number, style: "short" | "long" = "short"): string {
    return dateTimeFormat(`weekday-${style}`, { weekday: style, timeZone: "UTC" }).format(new Date(Date.UTC(2024, 0, iso)));
  }

  function number(value: number, options?: Intl.NumberFormatOptions): string {
    return new Intl.NumberFormat(locale, options).format(value);
  }

  /**
   * What a person typed ("250", "1,250.50", "$250", "1.250,50" in de-DE) to integer minor units, by the club's own separators; null when it
   * is not money in this locale and currency. Strict on purpose: a group separator must sit where the locale puts one (so "12,50" is
   * refused in en-US, not read as 1,250), "1234.56" is refused in de-DE (where "." groups), trailing text ("12abc") is refused, and more
   * decimals than the currency has ("1.005" dollars, any decimals for yen) are refused rather than silently rounded.
   */
  function parseMoney(text: string): number | null {
    const digits = currencyDigits(locale, currency);
    const parts = new Intl.NumberFormat(locale, { style: "decimal", minimumFractionDigits: 1 }).formatToParts(1234567.5);
    const decimal = parts.find((part) => part.type === "decimal")?.value ?? ".";
    const symbols = new Set(
      new Intl.NumberFormat(locale, { style: "currency", currency }).formatToParts(1).filter((part) => part.type === "currency").map((part) => part.value),
    );
    symbols.add(currency);
    let body = text.replace(/[\s\u00a0\u202f]+/g, " ").trim();
    let negative = false;
    if (body.startsWith("-") || body.startsWith("\u2212")) {
      negative = true;
      body = body.slice(1).trim();
    }
    for (const symbol of symbols) {
      if (body.startsWith(symbol)) body = body.slice(symbol.length).trim();
      else if (body.endsWith(symbol)) body = body.slice(0, body.length - symbol.length).trim();
    }
    if (!body) return null;
    if (!/^[\d '\u2019.,]+$/.test(body)) return null;
    const pieces = body.split(decimal);
    if (pieces.length > 2) return null;
    const whole = pieces[0] ?? "";
    const fraction = pieces[1] ?? "";
    if (pieces.length === 2 && (fraction.length === 0 || fraction.length > digits || !/^\d+$/.test(fraction))) return null;
    const plain = whole.replace(/[ '\u2019.,]/g, "");
    if (!/^\d+$/.test(plain)) return null;
    // Group separators are accepted only where this locale would write them (its own grouping, whatever the size: 1,23,456 in en-IN).
    if (plain !== whole) {
      const canonical = (value: string) => value.replace(/[\s\u00a0\u202f]/g, " ").replace(/\u2019/g, "'");
      const expected = new Intl.NumberFormat(locale, { style: "decimal", useGrouping: true, maximumFractionDigits: 0 }).format(Number(plain));
      if (canonical(expected) !== canonical(whole)) return null;
    }
    const minor = Number(plain + fraction.padEnd(digits, "0"));
    if (!Number.isSafeInteger(minor)) return null;
    return negative ? -minor : minor;
  }

  /** An example of what to type for an amount in this club's conventions ("25.00", "25,00", "25" for yen). */
  function moneyExample(): string {
    return moneyInput(25 * 10 ** currencyDigits(locale, currency));
  }

  /** Minor units to the plain editable text in the club's conventions ("250.00"). */
  function moneyInput(minor: number): string {
    const digits = currencyDigits(locale, currency);
    return new Intl.NumberFormat(locale, {
      style: "decimal",
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      useGrouping: false,
    }).format(minor / 10 ** digits);
  }

  /** A length of time such as "2 min 5 sec" in the interface's conventions: the two largest units that are not zero. */
  function duration(milliseconds: number): string {
    const unit = (value: number, name: "hour" | "minute" | "second" | "millisecond") =>
      new Intl.NumberFormat(locale, { style: "unit", unit: name, unitDisplay: "short", maximumFractionDigits: 0 }).format(value);
    const total = Math.max(0, Math.round(milliseconds));
    if (total < 1000) return unit(total, "millisecond");
    const seconds = Math.round(total / 1000);
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const rest = seconds % 60;
    const parts: string[] = [];
    if (hours) {
      parts.push(unit(hours, "hour"));
      if (minutes) parts.push(unit(minutes, "minute"));
    } else if (minutes) {
      parts.push(unit(minutes, "minute"));
      if (rest) parts.push(unit(rest, "second"));
    } else {
      parts.push(unit(rest, "second"));
    }
    return parts.join(" ");
  }

  return { locale, timeZone, currency, money, duration, date, monthDay, monthNames, dateTime, time, wallTime, weekday, number, parseMoney, moneyInput, moneyExample };
}

export type Formatters = ReturnType<typeof createFormatters>;
